import { productStore } from './productStore';
import { BUDGET_SECTIONS, BudgetSection, asAmount, newLineId } from './budget';

/** A quarter, because these invoices are not sprint-shaped; stored per product and year. */
export const QUARTERS = ['q1', 'q2', 'q3', 'q4'] as const;
export type Quarter = (typeof QUARTERS)[number];

export interface QuarterLine {
  /** Its own id, so two lines that end up with the same name are still two lines. */
  id: string;
  name: string;
  /** EUR for the quarter. A quarter with no entry is not budgeted for, which is not the same as zero. */
  amounts: Partial<Record<Quarter, number>>;
}

export type QuarterCosts = Record<BudgetSection, QuarterLine[]>;

/** Budget and spend are the same shape, kept apart so what was planned survives what was paid. */
export type CostKind = 'budget' | 'spend';

export const emptyQuarterCosts = (): QuarterCosts => ({
  hosting: [],
  advertising: [],
  varia: [],
});

export const quarterCostsKey = (kind: CostKind, productId: string, year: string): string =>
  `productQuarterCosts:${kind}:${productId}:${year}`;

/** Which quarter a date falls in, from a `YYYY-MM-DD` day or the front of an instant. */
export const quarterOf = (date: string): Quarter => QUARTERS[Math.floor((Number(date.slice(5, 7)) - 1) / 3)] ?? 'q1';

const parseLine = (raw: unknown): QuarterLine | null => {
  if (!raw || typeof raw !== 'object') return null;
  const stored = raw as Partial<QuarterLine>;
  const name = typeof stored.name === 'string' ? stored.name.trim() : '';
  if (!name) return null;
  const amounts: QuarterLine['amounts'] = {};
  QUARTERS.forEach(quarter => {
    const amount = asAmount((stored.amounts ?? {})[quarter]);
    if (amount !== null) amounts[quarter] = amount;
  });
  return { id: typeof stored.id === 'string' ? stored.id : newLineId(), name, amounts };
};

const parse = (raw: unknown): QuarterCosts | null => {
  if (!raw || typeof raw !== 'object') return null;
  const stored = raw as Partial<Record<BudgetSection, unknown>>;
  const costs = emptyQuarterCosts();
  BUDGET_SECTIONS.forEach(section => {
    const lines = stored[section];
    if (!Array.isArray(lines)) return;
    costs[section] = lines.map(parseLine).filter((line): line is QuarterLine => line !== null);
  });
  return costs;
};

// A value that does not parse reads as an empty year.
export const readQuarterCosts = (kind: CostKind, productId: string, year: string): QuarterCosts => {
  try {
    const raw = productStore.getItem(quarterCostsKey(kind, productId, year));
    return (raw ? parse(JSON.parse(raw)) : null) ?? emptyQuarterCosts();
  } catch {
    return emptyQuarterCosts();
  }
};

export const writeQuarterCosts = (kind: CostKind, productId: string, year: string, costs: QuarterCosts): void => {
  productStore.setItem(quarterCostsKey(kind, productId, year), JSON.stringify(costs));
};

/** What a section comes to in one quarter. */
export const sumQuarter = (lines: QuarterLine[], quarter: Quarter): number =>
  Math.round(lines.reduce((total, line) => total + (line.amounts[quarter] ?? 0), 0) * 100) / 100;
